export const en = {
	common: {
		appName: 'BrewAssistant',
		loading: 'Loading...',
		save: 'Save',
		cancel: 'Cancel',
		delete: 'Delete',
		edit: 'Edit',
		create: 'Create',
		back: 'Back',
		next: 'Next',
		submit: 'Submit',
		search: 'Search',
		filter: 'Filter',
		close: 'Close',
		confirm: 'Confirm',
		yes: 'Yes',
		no: 'No',
		error: 'Error',
		success: 'Success',
		warning: 'Warning',
		info: 'Info'
	},
	nav: {
		home: 'Home',
		batches: 'Batches',
		recipes: 'Recipes',
		aiAssistant: 'AI Assistant',
		dashboard: 'My Account',
		admin: 'Admin',
		login: 'Sign In',
		logout: 'Sign Out',
		account: 'Account',
		preferences: 'Preferences',
		darkMode: 'Dark Mode',
		lightMode: 'Light Mode',
		navigation: 'Navigation',
		subtitle: 'Home Brewing Assistant',
		activateDarkMode: 'Activate dark mode',
		activateLightMode: 'Activate light mode',
		language: 'Language'
	},
	auth: {
		signIn: 'Sign In',
		signUp: 'Sign Up',
		email: 'Email',
		password: 'Password',
		name: 'Name',
		country: 'Country',
		selectCountry: 'Select your country',
		confirmPassword: 'Confirm Password',
		forgotPassword: 'Forgot Password?',
		confirmCode: 'Confirmation Code',
		confirmSignUp: 'Confirm Sign Up',
		createAccount: 'Create Account',
		backToLogin: 'Back to Login',
		entering: 'Signing in...',
		creating: 'Creating...',
		confirming: 'Confirming...',
		emailPlaceholder: 'you@example.com',
		passwordPlaceholder: 'Your password',
		codePlaceholder: 'Code sent to your email',
		sessionExpired: 'Session expired. Please sign in again.',
		welcomeBack: 'Welcome back!',
		getStarted: 'Get started with BrewAssistant',
		alreadyHaveAccount: 'Already have an account?',
		dontHaveAccount: "Don't have an account?",
		acceptTerms: 'I accept the Terms & Conditions'
	},
	home: {
		title: 'BrewAssistant - AI-Powered Home Brewing Assistant',
		subtitle: 'Use artificial intelligence to better understand your recipes and beer batches. Integrate with Brewfather and get valuable insights about your brewing process.',
		viewBatches: 'View My Batches',
		tryAI: 'Try AI',
		brewfatherIntegration: 'Brewfather Integration',
		brewfatherDescription: 'Connect your Brewfather account and automatically sync your batches and recipes.',
		aiAnalysis: 'AI Analysis',
		aiDescription: 'Get intelligent insights about your batches and recipes using artificial intelligence.',
		recommendations: 'Recommendations',
		recommendationsDescription: 'Receive personalized suggestions to improve your recipes and processes.',
		features: 'Main Features:',
		batchManagement: 'Batch Management',
		batchManagementDescription: 'Track all your beer batches in one place.',
		recipeLibrary: 'Recipe Library',
		recipeLibraryDescription: 'Organize and manage your favorite recipes.',
		aiAssistant: 'AI Assistant',
		aiAssistantDescription: 'Ask questions and get intelligent answers about home brewing.',
		howItWorks: 'How it works:',
		step1: 'Connect your Brewfather account to sync batches and recipes',
		step2: 'Use the AI assistant to ask questions about your batches and recipes',
		step3: 'Receive analysis and personalized recommendations to improve your results',
		getStarted: 'Get Started',
		createAccount: 'Create Account',
		startNow: 'Start Now',
		createFreeAccount: 'Create your free account and start using BrewAssistant today'
	},
	batches: {
		title: 'My Batches',
		noBatches: 'No batches found',
		createFirst: 'Create First Batch',
		newBatch: 'New Batch',
		syncBrewfather: 'Sync Brewfather',
		syncing: 'Syncing...',
		errorLoading: 'Error loading batches',
		errorSyncing: 'Error syncing with Brewfather',
		syncSuccess: 'Batches synced successfully!',
		noCredentials: 'Brewfather credentials not configured',
		noCredentialsDescription: 'Please configure your Brewfather API credentials in the Dashboard to view your batches.',
		goToDashboard: 'Go to Dashboard',
		batchNotFound: 'Batch not found',
		backToBatches: 'Back to Batches',
		measurements: 'Measurements',
		originalGravity: 'Original Gravity',
		finalGravity: 'Final Gravity',
		notes: 'Notes',
		events: 'Events',
		estimated: 'Estimated',
		expected: 'Expected',
		attenuation: 'Attenuation',
		efficiency: 'Efficiency',
		active: 'Active',
		viewRecipe: 'View Recipe',
		fermentationDuration: 'Fermentation Duration',
		fermenting: 'fermenting',
		day: 'day',
		days: 'days',
		daysRemaining: 'days remaining',
		complete: 'Complete',
		ago: 'ago',
		remaining: 'remaining',
		today: 'Today',
		overdue: 'Overdue',
		askAIAboutBatch: 'Ask AI About This Batch',
		hideAIChat: 'Hide AI Chat',
		status: {
			planned: 'Planned',
			brewing: 'Brewing',
			fermenting: 'Fermenting',
			conditioning: 'Conditioning',
			completed: 'Completed',
			archived: 'Archived'
		},
		recipe: 'Recipe',
		brewDate: 'Brew Date',
		bottlingDate: 'Bottling Date'
	},
	recipes: {
		title: 'My Recipes',
		noRecipes: 'No recipes found',
		createFirst: 'Create First Recipe',
		newRecipe: 'New Recipe',
		syncBrewfather: 'Sync Brewfather',
		syncing: 'Syncing...',
		errorLoading: 'Error loading recipes',
		errorSyncing: 'Error syncing with Brewfather',
		syncSuccess: 'Recipes synced successfully!',
		noCredentials: 'Brewfather credentials not configured',
		noCredentialsDescription: 'Please configure your Brewfather API credentials in the Dashboard to view your recipes.',
		goToDashboard: 'Go to Dashboard',
		type: {
			'all-grain': 'All Grain',
			extract: 'Extract',
			'partial-mash': 'Partial Mash',
			biab: 'BIAB'
		},
		style: 'Style'
	},
	dashboard: {
		title: 'My Account',
		userInfo: 'User Information',
		brewfatherIntegration: 'Brewfather Integration',
		brewfatherUserId: 'Brewfather User ID',
		brewfatherUserIdHelper: 'Find your User ID in Brewfather API settings',
		brewfatherApiKey: 'Brewfather API Key',
		brewfatherApiKeyHelper: 'Keep your API Key secure and do not share it with anyone',
		saveCredentials: 'Save Credentials',
		credentialsSaved: 'Brewfather credentials saved!',
		setupGuide: 'How to Get API Credentials',
		openaiIntegration: 'OpenAI API Key (optional)',
		openaiDescription: 'Use your own OpenAI API key for AI features (assistant, batch analysis). If not set, the default key is used.',
		openaiApiKey: 'OpenAI API Key',
		openaiApiKeyHelper: 'Get your key from platform.openai.com. Your key is stored securely and only used for your requests.',
		openaiSaved: 'OpenAI API key saved.',
		openaiDeleted: 'OpenAI API key removed.',
		subscription: 'Subscription',
		subscriptionPlan: 'Plan',
		freePlan: 'Free plan',
		youAreOnPlan: "You're on the {plan} plan",
		changePlan: 'Change',
		manageSubscription: 'Manage subscription'
	},
	brewfatherSetup: {
		title: 'Brewfather API Setup Guide',
		introduction: 'Introduction',
		introductionText: 'To use BrewAssistant with your Brewfather account, you need to obtain your API credentials. This guide will walk you through the process step by step.',
		step1Title: 'Step 1: Access Brewfather Settings',
		step1Description: 'Log in to your Brewfather account and navigate to the API settings page. You can access it directly from your account settings.',
		openBrewfatherSettings: 'Open Brewfather API Settings',
		step2Title: 'Step 2: Find Your API Key',
		step2Description: 'In the API settings page, you will find your API Key. This is a long string of characters that acts as a password for API access. Copy this key.',
		apiKeyLocation: 'Settings → API → API Key',
		step3Title: 'Step 3: Find Your User ID',
		step3Description: 'On the same API settings page, you will also find your User ID. This is typically displayed near the API Key. Copy this ID as well.',
		userIdLocation: 'Settings → API → User ID',
		step4Title: 'Step 4: Enter Credentials in BrewAssistant',
		step4Description: 'Once you have both your API Key and User ID, go to the Dashboard in BrewAssistant and enter them in the Brewfather Integration section.',
		checklist1: 'API Key is copied correctly',
		checklist2: 'User ID is copied correctly',
		checklist3: 'Both credentials are saved in the Dashboard',
		importantNotes: 'Important Notes',
		note1Title: 'Security',
		note1Text: 'Your API Key is sensitive information. Never share it publicly or commit it to version control. It provides full access to your Brewfather data.',
		note2Title: 'API Key Regeneration',
		note2Text: 'If you suspect your API Key has been compromised, you can regenerate it in Brewfather settings. You will need to update it in BrewAssistant after regeneration.',
		note3Title: 'Data Access',
		note3Text: 'The API Key allows BrewAssistant to read your batches and recipes in real-time. No data is stored permanently in BrewAssistant - everything is fetched directly from Brewfather.',
		goToDashboard: 'Go to Dashboard'
	},
	openaiSetup: {
		title: 'OpenAI API Key Setup Guide',
		introduction: 'Introduction',
		introductionText: 'To use your own OpenAI API key for AI features (assistant, batch analysis), you need to create an OpenAI account and generate an API key. This guide will walk you through the process step by step.',
		step1Title: 'Step 1: Create an OpenAI Account',
		step1Description: 'If you don\'t have an OpenAI account yet, sign up at platform.openai.com. You\'ll need to provide payment information as OpenAI charges based on usage.',
		createOpenAIAccount: 'Create OpenAI Account',
		step2Title: 'Step 2: Navigate to API Keys',
		step2Description: 'Once logged in, go to the API Keys section in your OpenAI account. This is where you can create and manage your API keys.',
		openAPIKeysPage: 'Open API Keys Page',
		apiKeyLocation: 'Platform → API Keys → Create new secret key',
		step3Title: 'Step 3: Create a New API Key',
		step3Description: 'Click "Create new secret key" and give it a name (e.g., "BrewAssistant"). Copy the key immediately - you won\'t be able to see it again! The key will start with "sk-".',
		apiKeyFormat: 'Format: sk-... (starts with "sk-" followed by a long string)',
		step4Title: 'Step 4: Enter API Key in BrewAssistant',
		step4Description: 'Once you have your API key, go to the Dashboard in BrewAssistant and enter it in the OpenAI Integration section. Your key will be stored securely and only used for your requests.',
		checklist1: 'API Key is copied correctly (starts with "sk-")',
		checklist2: 'API Key is saved in the Dashboard',
		checklist3: 'You can test it by using the AI Assistant or running a batch analysis',
		importantNotes: 'Important Notes',
		note1Title: 'Security',
		note1Text: 'Your API Key is sensitive information. Never share it publicly, commit it to version control, or expose it in client-side code. It provides access to your OpenAI account and usage.',
		note2Title: 'Usage & Billing',
		note2Text: 'OpenAI charges based on API usage. You can monitor your usage and set spending limits in your OpenAI account settings. BrewAssistant uses your key only for your requests.',
		note3Title: 'Optional',
		note3Text: 'Using your own API key is optional. If you don\'t set one, BrewAssistant will use a default key. However, using your own key gives you more control over usage and billing.',
		note4Title: 'Key Management',
		note4Text: 'You can create multiple API keys, revoke them, or regenerate them at any time in your OpenAI account. If you revoke a key, update it in BrewAssistant to continue using AI features.',
		goToDashboard: 'Go to Dashboard'
	},
	aiAssistant: {
		title: 'AI Assistant',
		description: 'Ask questions about home brewing, your batches and recipes. Use artificial intelligence to get valuable insights.',
		askQuestion: 'Ask a Question',
		questionPlaceholder: 'Ex: How can I improve the flavor of my IPA?',
		sendQuestion: 'Send Question',
		processing: 'Processing...',
		recentInsights: 'Recent Insights',
		questionHistory: 'Question History',
		noQuestions: 'No questions yet. Ask your first question!',
		errorAsking: 'Error asking question',
		answerReceived: 'Answer received!',
		insightTypes: {
			analysis: 'Analysis',
			suggestion: 'Suggestion',
			warning: 'Warning',
			tip: 'Tip'
		}
	},
	footer: {
		copyright: `© ${new Date().getFullYear()} BrewAssistant. All rights reserved.`,
		privacy: 'Privacy',
		terms: 'Terms',
		contact: 'Contact'
	},
	notFound: {
		title: '404',
		message: 'Page not found.',
		backHome: 'Back to Home'
	}
};
